const DEFAULT_TIMEOUT_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const DEFAULT_EVENTS = [
  'mousedown',
  'mousemove',
  'keypress',
  'scroll',
  'touchstart',
  'click',
  'focus'
];

function createSetTimeout() {
  if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
    return window.setTimeout.bind(window);
  }
  return setTimeout;
}

function createClearTimeout() {
  if (typeof window !== 'undefined' && typeof window.clearTimeout === 'function') {
    return window.clearTimeout.bind(window);
  }
  return clearTimeout;
}

export class InactivityMonitor {
  constructor({
    documentRef = typeof window !== 'undefined' ? window.document : null,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    events = DEFAULT_EVENTS,
    onTimeout = () => {},
    getAuthState = () => ({ isLoggedIn: false }),
    throttleMs = 1000,
    setTimer = createSetTimeout(),
    clearTimer = createClearTimeout(),
    now = () => Date.now(),
  } = {}) {
    this.document = documentRef;
    this.timeoutMs = typeof timeoutMs === 'number' && timeoutMs > 0 ? timeoutMs : DEFAULT_TIMEOUT_MS;
    this.events = Array.isArray(events) && events.length ? [...events] : [...DEFAULT_EVENTS];
    this.onTimeout = typeof onTimeout === 'function' ? onTimeout : () => {};
    this.getAuthState = typeof getAuthState === 'function' ? getAuthState : () => ({ isLoggedIn: false });
    this.throttleMs = typeof throttleMs === 'number' && throttleMs >= 0 ? throttleMs : 1000;
    this.setTimer = setTimer;
    this.clearTimer = clearTimer;
    this.now = now;

    this.running = false;
    this.timerId = null;
    this.throttleId = null;
    this.listeners = [];
  }

  start() {
    if (!this.document) {
      return false;
    }

    if (!this.isUserLoggedIn()) {
      return false;
    }

    if (this.running) {
      this.stop();
    }

    this.running = true;
    const handler = () => this.handleActivity();
    this.events.forEach((event) => {
      this.document.addEventListener(event, handler, true);
      this.listeners.push({ event, handler });
    });

    this.reset();
    return true;
  }

  stop() {
    if (!this.running) {
      return false;
    }

    this.running = false;

    this.listeners.forEach(({ event, handler }) => {
      this.document.removeEventListener(event, handler, true);
    });
    this.listeners = [];

    if (this.timerId) {
      this.clearTimer(this.timerId);
      this.timerId = null;
    }

    if (this.throttleId) {
      this.clearTimer(this.throttleId);
      this.throttleId = null;
    }

    return true;
  }

  reset() {
    if (!this.running) {
      return false;
    }

    if (!this.isUserLoggedIn()) {
      this.stop();
      return false;
    }

    if (this.timerId) {
      this.clearTimer(this.timerId);
    }

    this.timerId = this.setTimer(() => {
      this.timerId = null;
      this.handleTimeout();
    }, this.timeoutMs);

    return true;
  }

  handleActivity() {
    if (!this.running) {
      return;
    }

    if (this.throttleMs === 0) {
      this.reset();
      return;
    }

    if (this.throttleId) {
      return;
    }

    this.throttleId = this.setTimer(() => {
      this.throttleId = null;
      this.reset();
    }, this.throttleMs);
  }

  handleTimeout() {
    if (!this.running) {
      return;
    }

    this.stop();

    try {
      this.onTimeout();
    } catch (error) {
      // Swallow errors from timeout handler to avoid crashing the monitor.
    }
  }

  isUserLoggedIn() {
    try {
      const state = this.getAuthState();
      return Boolean(state && state.isLoggedIn);
    } catch (error) {
      return false;
    }
  }
}


if (typeof window !== 'undefined') {

  window.AgileProServices = window.AgileProServices || {};

  window.AgileProServices.InactivityMonitor =

    window.AgileProServices.InactivityMonitor || InactivityMonitor;

}

